import { SvgIconProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import camelCase from "lodash/camelCase";
import React, { HTMLAttributes } from "react";

import { ProductContentAvailableDaysIcon } from "../components/Icon/ProductContentAvailableDaysIcon";
import { ProductContentExclusionIcon } from "../components/Icon/ProductContentExclusionIcon";
import { ProductContentExhibitionIcon } from "../components/Icon/ProductContentExhibitionIcon";
import { ProductContentFinishingPointIcon } from "../components/Icon/ProductContentFinishingPointIcon";
import { ProductContentHighlightsIcon } from "../components/Icon/ProductContentHighlightsIcon";
import { ProductContentInclusionIcon } from "../components/Icon/ProductContentInclusionIcon";
import { ProductContentItineraryIcon } from "../components/Icon/ProductContentItineraryIcon";
import { ProductContentLocationNoteIcon } from "../components/Icon/ProductContentLocationNoteIcon";
import { ProductContentMeetingPointIcon } from "../components/Icon/ProductContentMeetingPointIcon";
import { ProductContentNoteIcon } from "../components/Icon/ProductContentNoteIcon";
import { ProductContentOpeningHoursIcon } from "../components/Icon/ProductContentOpeningHoursIcon";
import { ProductContentRequirementIcon } from "../components/Icon/ProductContentRequirementIcon";
import { ProductContentRootIcon } from "../components/Icon/ProductContentRootIcon";
import { ProductContentSafetyMeasureIcon } from "../components/Icon/ProductContentSafetyMeasureIcon";
import { ProductContentTimingNoteIcon } from "../components/Icon/ProductContentTimingNoteIcon";
import { ProductContentVenueIcon } from "../components/Icon/ProductContentVenueIcon";
import { LabeledSection } from "../components/LabeledSection";
import { useProductTranslation } from "../hooks/useProductTranslation";
import { ProductContentType } from "../types/ProductContent";

export type ProductSectionProps = {
    titleTranslationKey?: string;
    type: ProductContentSectionType;
} & HTMLAttributes<HTMLElement>;

export type ProductContentSectionType = Exclude<
    ProductContentType,
    "CONTACT_PRICING_DESCRIPTION" | "VOUCHER_NOTE" | "STARTING_POINT"
>;

const ICONS: Record<ProductContentSectionType, React.ComponentType<SvgIconProps>> = {
    HIGHLIGHT: ProductContentHighlightsIcon,
    ROOT: ProductContentRootIcon,
    ITINERARY: ProductContentItineraryIcon,
    INCLUSION: ProductContentInclusionIcon,
    EXCLUSION: ProductContentExclusionIcon,
    VENUE: ProductContentVenueIcon,
    EXHIBITION: ProductContentExhibitionIcon,
    OPENING_HOURS: ProductContentOpeningHoursIcon,
    AVAILABLE_DAY: ProductContentAvailableDaysIcon,
    SAFETY_MEASURE: ProductContentSafetyMeasureIcon,
    MEETING_POINT: ProductContentMeetingPointIcon,
    FINISHING_POINT: ProductContentFinishingPointIcon,
    TIMING_NOTE: ProductContentTimingNoteIcon,
    LOCATION_NOTE: ProductContentLocationNoteIcon,
    REQUIREMENT: ProductContentRequirementIcon,
    NOTE: ProductContentNoteIcon,
};

function getTranslationKeyForSection(section: ProductContentSectionType, titleTranslationKey?: string) {
    return `label.${titleTranslationKey ?? camelCase(section)}`;
}

const LabeledSectionContent = styled("div")({
    wordBreak: "break-word",
});

export function ProductContentSection({ type, titleTranslationKey, children, ...props }: ProductSectionProps) {
    const Icon = type in ICONS ? ICONS[type] : undefined;

    const [t] = useProductTranslation();
    const translatedTitle = t(getTranslationKeyForSection(type, titleTranslationKey));

    return (
        <LabeledSection title={translatedTitle} Icon={Icon} {...props}>
            <LabeledSectionContent>{children}</LabeledSectionContent>
        </LabeledSection>
    );
}
